import {useEffect} from "react";
import {Platform, ScrollView} from "react-native";
import {useApolloClient, ApolloError} from "@apollo/client";
import {string} from "yup";
import {
    Box,
    Form,
    FormGroup,
    Stack,
    SubmitButton,
    TextInput,
    Text,
    View,
    ErrorMessage,
} from "@unibuddy/patron";

import {useErrorReporting} from "@unibuddy/error-reporting";
import {usePageView, useAnalytics} from "@unibuddy/tracking";
import {useFormikContext, FormikHelpers} from "formik";

import {boldFontStyles} from "ubcommunity-shared/src/Styles/fontStyles";
import {
    PageViews,
    TrackEvents,
    TrackSources,
} from "ubcommunity-shared/src/constants/trackingConstants";
import {useEmailInvite} from "ubcommunity-shared/src/Auth/EmailInviteProvider/EmailInviteProvider";
import {PasswordInput} from "ubcommunity-shared/src/Auth/PasswordInput/PasswordInput";
import {MutationErrorHandler} from "ubcommunity-shared/src/General/Errors/MutationErrorHandler";
import {useRegisterCommunityApplicantMutation} from "ubcommunity-shared/src/types";
import {SocialLogin} from "ubcommunity-shared/src/Auth/SSO/SocialLogin";
import {useIsFeatureEnabledBoolean} from "ubcommunity-shared/src/Hooks/useIsFeatureEnabledBoolean";
import {SSO_FEATURE_FLAG} from "ubcommunity-shared/src/featureFlags";

import {StepIndicator} from "./StepIndicator";
import {RegisterHeader} from "./RegisterHeader";
import {loginQuery} from "../LoginForm/LoginForm";
import useAuth from "../hooks/useAuth";
import {TermsAndConditions} from "./TermsAndConditions";
import {SignupInvitedEmailPrompt} from "./SignupInvitedEmailPrompt";

export const REGISTER_TOTAL_STEPS = 2;
export const PASSWORD_MIN_LENGTH = 12;

export enum fieldNames {
    FIRST_NAME = "firstName",
    LAST_NAME = "lastName",
    EMAIL = "email",
    PASSWORD = "password",
}

type FormValues = {[K in fieldNames]: string};

const initialValues: FormValues = {
    [fieldNames.FIRST_NAME]: "",
    [fieldNames.LAST_NAME]: "",
    [fieldNames.EMAIL]: "",
    [fieldNames.PASSWORD]: "",
};

export const fieldLabels = {
    [fieldNames.FIRST_NAME]: "First name",
    [fieldNames.LAST_NAME]: "Last name",
    [fieldNames.EMAIL]: "Email",
    [fieldNames.PASSWORD]: "Password",
};

export const fieldErrorMessages = {
    [fieldNames.FIRST_NAME]: "First name is a required field",
    [fieldNames.LAST_NAME]: "Last name is a required field",
    [fieldNames.EMAIL]: "Email is a required field",
    [fieldNames.PASSWORD]: "Password is a required field",
};

const validationSchema = {
    [fieldNames.FIRST_NAME]: string().required(fieldErrorMessages[fieldNames.FIRST_NAME]),
    [fieldNames.LAST_NAME]: string().required(fieldErrorMessages[fieldNames.LAST_NAME]),
    [fieldNames.EMAIL]: string().email().required(fieldErrorMessages[fieldNames.EMAIL]),
    [fieldNames.PASSWORD]: string()
        .required(fieldErrorMessages[fieldNames.PASSWORD])
        .min(PASSWORD_MIN_LENGTH),
};

const SignUpStepOneForm = ({invitedEmail, error}: {invitedEmail: string; error: ApolloError}) => {
    const {touched, errors, setFieldValue} = useFormikContext<FormValues>();
    const {trackEvent} = useAnalytics();

    const firstNameError = errors[fieldNames.FIRST_NAME];
    const firstNameHasError = touched[fieldNames.FIRST_NAME] && firstNameError;
    const lastNameError = errors[fieldNames.LAST_NAME];
    const lastNameHasError = touched[fieldNames.LAST_NAME] && lastNameError;
    const emailError = errors[fieldNames.EMAIL];
    const emailHasError = touched[fieldNames.EMAIL] && emailError;
    const passwordError = errors[fieldNames.PASSWORD];
    const passwordHasError = touched[fieldNames.PASSWORD] && passwordError;

    const socialLoginEnabledFeatureFlag = useIsFeatureEnabledBoolean(SSO_FEATURE_FLAG);

    useEffect(() => {
        if (invitedEmail) {
            setFieldValue(fieldNames.EMAIL, invitedEmail);
        }
    }, [invitedEmail, setFieldValue]);

    useEffect(() => {
        if (firstNameHasError) {
            trackEvent(TrackEvents.FORM_VALIDATION_ERROR, {
                form: TrackSources.SIGNUP_FLOW,
                field: fieldNames.FIRST_NAME,
                error: firstNameError,
            });
        }
    }, [firstNameError, firstNameHasError, trackEvent]);

    useEffect(() => {
        if (lastNameHasError) {
            trackEvent(TrackEvents.FORM_VALIDATION_ERROR, {
                form: TrackSources.SIGNUP_FLOW,
                field: fieldNames.LAST_NAME,
                error: lastNameError,
            });
        }
    }, [lastNameError, lastNameHasError, trackEvent]);

    useEffect(() => {
        if (emailHasError) {
            trackEvent(TrackEvents.FORM_VALIDATION_ERROR, {
                form: TrackSources.SIGNUP_FLOW,
                field: fieldNames.EMAIL,
                error: emailError,
            });
        }
    }, [emailError, emailHasError, trackEvent]);

    useEffect(() => {
        if (passwordHasError) {
            trackEvent(TrackEvents.FORM_VALIDATION_ERROR, {
                form: TrackSources.SIGNUP_FLOW,
                field: fieldNames.PASSWORD,
                error: passwordError,
            });
        }
    }, [passwordError, passwordHasError, trackEvent]);

    return (
        <View padding="medium-large" paddingTop="large">
            <Stack space="large">
                <RegisterHeader invitedEmail={invitedEmail} />
                <SignupInvitedEmailPrompt />
                {socialLoginEnabledFeatureFlag ? <SocialLogin /> : null}
                <Stack space="medium">
                    <FormGroup label={fieldLabels[fieldNames.FIRST_NAME]}>
                        <TextInput
                            {...Platform.select({
                                web: {
                                    autoComplete: "given-name",
                                },
                                ios: {
                                    autoComplete: "given-name",
                                    importantForAutofill: "yes",
                                },
                                android: {
                                    autoComplete: "name-given",
                                    importantForAutofill: "yes",
                                },
                            })}
                            name={fieldNames.FIRST_NAME}
                            required
                        />
                    </FormGroup>

                    <FormGroup label={fieldLabels[fieldNames.LAST_NAME]}>
                        <TextInput
                            {...Platform.select({
                                web: {
                                    autoComplete: "family-name",
                                },
                                ios: {
                                    autoComplete: "family-name",
                                    importantForAutofill: "yes",
                                },
                                android: {
                                    autoComplete: "name-family",
                                    importantForAutofill: "yes",
                                },
                            })}
                            name={fieldNames.LAST_NAME}
                            required
                        />
                    </FormGroup>

                    {!invitedEmail ? (
                        <FormGroup label={fieldLabels[fieldNames.EMAIL]}>
                            <TextInput
                                {...Platform.select({
                                    web: {
                                        type: "email",
                                    },
                                    native: {
                                        type: "email",
                                        keyboardType: "email-address",
                                        importantForAutofill: "yes",
                                        textContentType: "emailAddress",
                                    },
                                })}
                                autoComplete="email"
                                name={fieldNames.EMAIL}
                                required
                            />
                        </FormGroup>
                    ) : null}

                    <PasswordInput
                        name={fieldNames.PASSWORD}
                        required
                        helpBottom={
                            <Box paddingY="small">
                                <Text size="small">
                                    Your password must be at least 12 characters
                                </Text>
                            </Box>
                        }
                        {...Platform.select({
                            web: {
                                autoCompleteType: "new-password",
                            },
                            native: {
                                textContentType: "newPassword",
                            },
                            ios: {
                                autocomplete: "new-password",
                            },
                            android: {
                                autocomplete: "password-new",
                            },
                        })}
                    />

                    {errors?.serverError ? (
                        <ErrorMessage
                            component={
                                <MutationErrorHandler
                                    error={error}
                                    meta={{
                                        component: "SignupStep1",
                                        mutation: "useRegisterCommunityApplicantMutation",
                                    }}
                                />
                            }
                            name="serverError"
                        />
                    ) : null}

                    <SubmitButton color="primary" block>
                        {/* @ts-ignore */}
                        <Text style={{...boldFontStyles}} size="large" color="white">
                            Next
                        </Text>
                    </SubmitButton>
                    <TermsAndConditions />
                </Stack>
            </Stack>
        </View>
    );
};

export default function SignupStep1() {
    const {invitedEmail} = useEmailInvite();
    const client = useApolloClient();
    const {trackEvent, aliasVisitor} = useAnalytics();
    const {setAuth} = useAuth();
    const {resetEmailInvite} = useEmailInvite();
    const {reportError} = useErrorReporting();

    const [registerApplicant, {error}] = useRegisterCommunityApplicantMutation();

    usePageView(PageViews.SIGNUP_STEP_1_B);

    const handleRegister = async (
        {firstName, lastName, email, password}: FormValues,
        {setFieldError, setSubmitting}: FormikHelpers<FormValues>,
    ) => {
        try {
            const {data: signupData} = await registerApplicant({
                variables: {
                    firstName,
                    lastName,
                    email,
                    password,
                },
            });

            if (signupData?.createCommunityApplicantV2?.error) {
                setFieldError("serverError", signupData.createCommunityApplicantV2.error);
                trackEvent(TrackEvents.FORM_SUBMISSION_ERROR, {
                    form: TrackSources.SIGNUP_FLOW,
                    error: signupData.createCommunityApplicantV2.error,
                });
                return;
            }

            const {data} = await client.query({
                query: loginQuery,
                fetchPolicy: "network-only",
                variables: {
                    email,
                    password,
                },
            });

            trackEvent(TrackEvents.PROSPECT_ACCOUNT_CREATED);
            await aliasVisitor(data.getToken.me.anyUser); // NOSONAR (type is wrong since we use our own adaptor)
            resetEmailInvite();
            setAuth(data.getToken);
        } catch (error) {
            if (!error.message?.includes("403 Forbidden")) {
                reportError(error);
            }
            setFieldError(
                "serverError",
                "You may have a Unibuddy account already. Try logging in instead.",
            );
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <>
            <StepIndicator.BarIndicator currentStep={1} totalSteps={2} />
            <Form
                name="registration"
                onSubmit={handleRegister}
                validationSchema={validationSchema}
                initialValues={{
                    ...initialValues,
                    ...(invitedEmail && {[fieldNames.EMAIL]: invitedEmail}),
                    serverError: "",
                }}
            >
                <ScrollView>
                    <SignUpStepOneForm invitedEmail={invitedEmail} error={error} />
                </ScrollView>
            </Form>
        </>
    );
}
